import React, { useState } from 'react';
import { Language, TranslationContent } from '../types';

interface HeaderProps {
  lang: Language;
  toggleLanguage: () => void;
  t: TranslationContent['nav'];
  onNavigate: (section: string) => void;
}

const Header: React.FC<HeaderProps> = ({ lang, toggleLanguage, t, onNavigate }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, sectionId: string) => {
    e.preventDefault();
    setMobileMenuOpen(false);
    onNavigate(sectionId);
    window.history.pushState(null, '', `#${sectionId}`);
  };

  // Nova classe de link com destaque azul vibrante, escala e sombra no hover
  const linkClass = "px-5 py-2.5 text-sm font-bold tracking-tight transition-all duration-300 rounded-xl whitespace-nowrap text-slate-700 hover:text-white hover:bg-indigo-600 hover:shadow-lg hover:shadow-indigo-100 hover:scale-105 active:scale-95";

  const NavLinks = () => (
    <>
      <a href="#hero" onClick={(e) => handleNavClick(e, 'hero')} className={linkClass}>
        Home
      </a>
      <a href="#what-i-do" onClick={(e) => handleNavClick(e, 'what-i-do')} className={linkClass}>
        {t.whatIDo}
      </a>
      <a href="#who-for" onClick={(e) => handleNavClick(e, 'who-for')} className={linkClass}>
        {t.whoFor}
      </a>
      <a href="#why-me" onClick={(e) => handleNavClick(e, 'why-me')} className={linkClass}>
        {t.whyMe}
      </a>
      <a href="#contact" onClick={(e) => handleNavClick(e, 'contact')} className={linkClass}>
        {t.contact}
      </a>
    </>
  );

  return (
    <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-slate-100">
      <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
        
        {/* Lado Esquerdo: Logótipo */}
        <div className="flex-1 flex justify-start">
          <a 
            href="#hero" 
            onClick={(e) => handleNavClick(e, 'hero')} 
            className="flex items-center space-x-2 group shrink-0"
          >
            <span className="text-xl font-bold tracking-tight text-slate-900">
              vitor<span className="text-indigo-600 group-hover:text-indigo-500 transition-colors">nfgoncalves</span>.pt
            </span>
          </a>
        </div>

        {/* Centro: Navegação (Centrada no ecrã) */}
        <nav className="hidden md:flex items-center justify-center space-x-2 flex-grow">
          <NavLinks />
        </nav>

        {/* Lado Direito: Idiomas e Menu Mobile */}
        <div className="flex-1 flex items-center justify-end space-x-4">
          <div className="bg-slate-100 p-1 rounded-xl flex items-center shadow-inner" role="group" aria-label="Language selector">
            <button 
              onClick={() => lang !== 'pt' && toggleLanguage()}
              aria-label={t.switchToPT}
              aria-pressed={lang === 'pt'}
              className={`px-3 py-1.5 text-[10px] font-extrabold rounded-lg transition-all ${
                lang === 'pt' ? 'bg-white text-indigo-700 shadow-sm' : 'text-slate-700 hover:text-slate-900'
              }`}
            >
              PT
            </button>
            <button 
              onClick={() => lang !== 'en' && toggleLanguage()}
              aria-label={t.switchToEN}
              aria-pressed={lang === 'en'}
              className={`px-3 py-1.5 text-[10px] font-extrabold rounded-lg transition-all ${
                lang === 'en' ? 'bg-white text-indigo-700 shadow-sm' : 'text-slate-700 hover:text-slate-900'
              }`}
            >
              EN
            </button>
          </div>

          <button 
            className="md:hidden p-2 text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 rounded-lg"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label={mobileMenuOpen ? t.closeMenu : t.openMenu}
            aria-expanded={mobileMenuOpen}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              {mobileMenuOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16m-7 6h7" />
              )}
            </svg>
          </button>
        </div>
      </div>

      {mobileMenuOpen && (
        <div className="md:hidden bg-white border-b border-slate-100 px-6 py-6 animate-in slide-in-from-top duration-200">
          <nav className="flex flex-col space-y-3">
            <NavLinks />
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;